import React from 'react';
import { Order, useDutchieStore, useI18n, useTheme } from '@springbig/shared';
import { Box, VStack, HStack, Pressable } from 'native-base';
import { Caption, H2 } from '~/shared/Typography';
import { Section, Card } from '~/shared/Theme';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';

export type OrderDetailProps = {
    order: Order;
    onCancel: () => void;
};

export const OrderDetailPage = ({
    order,
    onCancel,
}: OrderDetailProps) => {

    const {
        primary,
        button_primary,
        button_text,
        highlight_text
    } = useTheme();

    const { t } = useI18n();
    const { retailer } = useDutchieStore();

    const formatPrice = (price: number): string => {

        return (price / 100).toFixed(2);
    };

    return (
        <Section>
            <Pressable onPress={onCancel} p={4}>
                <HStack alignItems='center' space={2}>
                    <FontAwesomeIcon
                        color={highlight_text}
                        icon={faAngleLeft}
                    />
                    <Caption color={highlight_text} text='general.back' />
                </HStack>
            </Pressable>
            <VStack px={4} space={4}>
                <VStack
                    bg={button_primary}
                    rounded={'xl'}
                    p={4}
                    alignItems={'center'}
                    space={2}
                >
                    <Caption color={button_text} fontSize={18}>
                        { t('shop.order_number') } { order.orderNumber }
                    </Caption>
                    <Caption color={button_text} fontSize={18}>
                        { format(new Date(order.createdAt), 'EEEE, LLLL d') } { t('shop.at') } { format(new Date(order.createdAt), 'h:maaa') }
                    </Caption>
                </VStack>

                { order.delivery &&
                    <Card space={2}>
                        <H2 text={'shop.delivery'} />
                        <Caption>{ order.status }</Caption>
                    </Card>
                }

                { order.pickup &&
                    <Card space={2}>
                        <H2 text={'shop.pickup'} />
                        <Caption>{ order.dispensaryName }</Caption>
                        <Caption>{ retailer?.address }</Caption>
                    </Card>
                }

                <Card>
                    <H2 text={'shop.items'} />
                    { order.items.map((item, index) =>

                        <HStack key={index} alignItems={'center'} space={2}>
                            <Box w={1} h={1} bg={primary} rounded={'full'}></Box>
                            <VStack flex={1}>
                                <Caption fontSize={12}>
                                    { item.product.name }
                                </Caption>
                                <HStack alignSelf={'flex-end'} space={2}>
                                    <Caption>{ formatPrice(item.price) }</Caption>
                                    <Caption>{ t('shop.x') }{ item.quantity }</Caption>
                                    <Caption>{ formatPrice(item.subtotal) }</Caption>
                                </HStack>
                            </VStack>
                        </HStack>
                    )}

                    <Box w={'100%'} h={.5} bg={primary}></Box>

                    <VStack pt={4} space={1}>
                        <HStack justifyContent={'space-between'}>
                            <Caption text={'shop.subtotal'} />
                            <Caption>{ order.subtotal }</Caption>
                        </HStack>
                        { !!order?.fees?.total &&
                            <HStack justifyContent={'space-between'}>
                                <Caption text={'shop.fees'} />
                                <Caption>{ order.fees.total }</Caption>
                            </HStack>
                        }
                        { !!order?.taxes?.total &&
                            <HStack justifyContent={'space-between'}>
                                <Caption text={'shop.taxes'} />
                                <Caption>{ order.taxes.total }</Caption>
                            </HStack>
                        }
                        { !!order?.discounts?.total &&
                            <HStack justifyContent={'space-between'}>
                                <Caption text={'shop.discounts'} />
                                <Caption>-{ order.discounts.total }</Caption>
                            </HStack>
                        }
                        <HStack justifyContent={'space-between'}>
                            <Caption text={'shop.total'} />
                            <Caption>{ order.total }</Caption>
                        </HStack>
                    </VStack>
                </Card>
            </VStack>
        </Section>
    );
};
